import {
  TITLE_FONT_LIBRARY,
  TITLE_FONT_LIBRARY_BY_ID,
  buildTitleFontFaceCss,
  getTitleFontEntry,
  quoteFamily,
  resolveTitleFontSelection,
} from './fontLibrary'
import { describeTextClipStyle } from './textClipStyle'

const stylesheetLoads = new Map()
const inlineLoads = new Map()
const warnedFontIds = new Set()

function ensureBrowser() {
  return typeof window !== 'undefined' && typeof document !== 'undefined'
}

function ensureStylesheet(href) {
  if (!ensureBrowser() || !href) return Promise.resolve()
  if (stylesheetLoads.has(href)) return stylesheetLoads.get(href)

  const promise = new Promise((resolve, reject) => {
    const existing = Array.from(document.querySelectorAll('link[data-vyby-font-href]'))
      .find((node) => node.dataset.vybyFontHref === href)
    if (existing) {
      if (existing.dataset.loaded === 'true' || existing.sheet) {
        resolve()
        return
      }
      existing.remove()
    }

    const link = document.createElement('link')
    link.rel = 'stylesheet'
    // The app is cross-origin isolated (COEP: require-corp); a CORS request is
    // required for providers that don't send Cross-Origin-Resource-Policy.
    link.crossOrigin = 'anonymous'
    link.href = href
    link.dataset.vybyFontHref = href
    link.addEventListener('load', () => {
      link.dataset.loaded = 'true'
      resolve()
    }, { once: true })
    link.addEventListener('error', () => {
      link.remove()
      reject(new Error(`font stylesheet failed to load: ${href}`))
    }, { once: true })
    document.head.appendChild(link)
  }).catch((error) => {
    stylesheetLoads.delete(href)
    throw error
  })

  stylesheetLoads.set(href, promise)
  return promise
}

function inlineStyleId(entry) {
  return `vyby-font-inline-${entry.id}`
}

function ensureInlineFaces(entry) {
  if (!ensureBrowser() || !entry) return Promise.resolve()
  if (inlineLoads.has(entry.id)) return inlineLoads.get(entry.id)

  const promise = Promise.resolve().then(() => {
    if (document.getElementById(inlineStyleId(entry))) return
    const style = document.createElement('style')
    style.id = inlineStyleId(entry)
    style.textContent = buildTitleFontFaceCss(entry)
    document.head.appendChild(style)
  })

  inlineLoads.set(entry.id, promise)
  return promise
}

function resetInlineFaces(entry) {
  if (!ensureBrowser() || !entry) return
  inlineLoads.delete(entry.id)
  document.getElementById(inlineStyleId(entry))?.remove()
}

function ensureSourceLoaded(entry) {
  if (!entry) return Promise.resolve()
  if (entry.sourceType === 'stylesheet') return ensureStylesheet(entry.href)
  if (entry.sourceType === 'faces') return ensureInlineFaces(entry)
  return Promise.resolve()
}

function warnFontOnce(fontId, error) {
  if (warnedFontIds.has(fontId)) return
  warnedFontIds.add(fontId)
  console.warn('title font unavailable, using fallback', fontId, error || '')
}

export async function ensureTitleFontLoaded(fontId, requestedWeight = null, requestedStyle = 'normal') {
  const selection = resolveTitleFontSelection(fontId, requestedWeight, requestedStyle)
  const entry = TITLE_FONT_LIBRARY_BY_ID[selection.id] || getTitleFontEntry(selection.id)
  if (!ensureBrowser() || !document.fonts?.load) return { ...selection, loaded: false }

  const spec = `${selection.style} ${selection.weight} 32px ${quoteFamily(selection.family)}`
  let loaded = false
  let failure = null
  try {
    await ensureSourceLoaded(entry)
    const faces = await document.fonts.load(spec)
    loaded = faces.length
      ? faces.some((face) => face.status === 'loaded')
      : entry?.sourceType === 'local' && document.fonts.check(spec)
  } catch (error) {
    failure = error
  }

  if (loaded) {
    warnedFontIds.delete(selection.id)
  } else {
    if (entry?.sourceType === 'faces') resetInlineFaces(entry)
    warnFontOnce(selection.id, failure)
  }
  return { ...selection, loaded }
}

export async function preloadTitleFonts(fonts = []) {
  const specs = new Map()
  fonts.filter(Boolean).forEach((font) => {
    const spec = typeof font === 'string' ? { family: font } : font
    if (!spec.family) return
    const key = `${spec.family}|${spec.weight ?? ''}|${spec.style ?? ''}`
    specs.set(key, spec)
  })
  return Promise.all(
    Array.from(specs.values()).map((spec) =>
      ensureTitleFontLoaded(spec.family, spec.weight ?? null, spec.style || 'normal')
    )
  )
}

export function collectTitleFontSpecs(tracks = {}) {
  const specs = []
  Object.values(tracks || {}).forEach((track) => {
    Object.values(track?.strips || {}).forEach((strip) => {
      if (strip?.asset?.type !== 'text') return
      const values = strip.animation?.values || {}
      const base = {
        family: values['font.family'],
        weight: values['font.weight'],
        style: values['font.style'],
      }
      specs.push(base)
      ;(strip.animation?.keyframes || []).forEach((kf) => {
        const props = kf?.props || {}
        if (typeof props['font.family'] === 'undefined' && typeof props['font.weight'] === 'undefined') return
        specs.push({
          family: props['font.family'] ?? base.family,
          weight: props['font.weight'] ?? base.weight,
          style: props['font.style'] ?? base.style,
        })
      })
    })
  })
  return specs
}

function normalizeFaceFamily(family) {
  return String(family || '').replace(/^["']|["']$/g, '').trim().toLowerCase()
}

function facesForFamily(family) {
  if (!ensureBrowser() || !document.fonts) return []
  const target = normalizeFaceFamily(family)
  return Array.from(document.fonts).filter((face) => normalizeFaceFamily(face.family) === target)
}

async function auditTitleFont(entry) {
  const result = await ensureTitleFontLoaded(entry.id)
  const faces = facesForFamily(entry.family)
  let status = 'failed'
  if (result.loaded) status = faces.length ? 'loaded' : 'system'
  return {
    id: entry.id,
    provider: entry.provider,
    family: entry.family,
    status,
    declaredWeights: (entry.weights || []).join(','),
    fileWeights: Array.from(new Set(faces.map((face) => face.weight))).join(','),
  }
}

function activeStrip(store) {
  const project = store?.state?.project || {}
  const timeline = project.timeline || {}
  return project.tracks?.[timeline.activeTrack]?.strips?.[timeline.activeStrip] || null
}

export function installTitleFontDebugHelper(store = null) {
  if (!ensureBrowser()) return
  window.vybyTitleFonts = {
    list() {
      return TITLE_FONT_LIBRARY.map((entry) => ({
        id: entry.id,
        label: entry.label,
        family: entry.family,
        provider: entry.provider,
        weights: entry.weights,
      }))
    },
    get(fontId) {
      return getTitleFontEntry(fontId)
    },
    async load(fontId, weight = null, style = 'normal') {
      return ensureTitleFontLoaded(fontId, weight, style)
    },
    async audit() {
      const rows = await Promise.all(TITLE_FONT_LIBRARY.map((entry) => auditTitleFont(entry)))
      console.table(rows)
      const failed = rows.filter((row) => row.status === 'failed')
      console.log(`title fonts: ${rows.length - failed.length}/${rows.length} ok`, failed.map((row) => row.id))
      return rows
    },
    stripReport() {
      const strip = activeStrip(store)
      if (!strip || strip.asset?.type !== 'text') {
        console.warn('select a text clip first')
        return null
      }
      const report = describeTextClipStyle(strip)
      console.log('clip values', report.clip)
      console.table(report.keyframes)
      const keyed = report.keyframes.filter((kf) => Object.keys(kf).some((key) => key.startsWith('font.')))
      console.log(keyed.length ? `${keyed.length} keyframe(s) still hold font props` : 'ok: no keyframe holds font props')
      return report
    },
  }
}

